import { createPortal } from 'react-dom';
import { Button } from '../../components/ui';

export interface AgentSyncModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
  restartRequired: boolean;
  fileNames: string[];
}

export default function AgentSyncModal({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
  restartRequired,
  fileNames,
}: AgentSyncModalProps) {
  if (!open) return null;

  return createPortal(
    <div className="ck-overlay-root">
      <div
        onClick={isSubmitting ? undefined : onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
          background: 'var(--surface-overlay)',
        }}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-sync-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          zIndex: 41,
          width: '440px',
          maxWidth: '92vw',
          maxHeight: 'calc(100dvh - var(--sat) - var(--sab) - 32px)',
          overflowY: 'auto',
          transform: 'translate(-50%, -50%)',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-pop)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            padding: '18px 22px 12px',
            borderBottom: '1px solid var(--border-hairline)',
          }}
        >
          <div className="t-eyebrow" style={{ marginBottom: '4px' }}>
            Agents
          </div>
          <div
            id="agent-sync-title"
            style={{
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            {restartRequired ? 'Write to disk and restart gateway' : 'Write to disk'}
          </div>
        </header>

        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-body)', lineHeight: 1.5 }}>
            {restartRequired
              ? 'This queues a write to the Mac. The next sync tick applies it and restarts this agent’s gateway. In-flight work dies. The current chat can lose context.'
              : 'This queues a write to the Mac. The next sync tick applies it. No gateway restart — brain files reload on the next turn.'}
          </p>
          {fileNames.length > 0 ? (
            <ul
              style={{
                margin: 0,
                padding: '10px 14px',
                listStyle: 'none',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {fileNames.map((name) => (
                <li
                  key={name}
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-muted)',
                    padding: '3px 0',
                  }}
                >
                  {name}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <footer
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border-hairline)',
            display: 'flex',
            gap: '10px',
          }}
        >
          <Button
            variant={restartRequired ? 'danger' : 'primary'}
            icon={restartRequired ? 'refresh-cw' : 'check'}
            disabled={isSubmitting}
            onClick={onConfirm}
          >
            {isSubmitting ? 'Queuing…' : restartRequired ? 'Write and restart' : 'Write to disk'}
          </Button>
          <Button variant="ghost" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
        </footer>
      </section>
    </div>,
    document.body
  );
}
